import { jsx } from "panel";
import keyBy from "lodash/keyBy";
import { SvgIconEl } from "common/components/svg-icon";
import { TooltipEl } from "common/components/tooltip";
import { Placement } from "common/components/tooltip/types";
import { RevealableTextEditEl } from "common/components/revealable-text-editor";
import { InputSize } from "common/components/input/types";
import { ButtonEl } from "common/components/button";
import { Directions } from "common/components/drop-menu/types";
import { TextHighlightEl } from "common/components/text-highlight";
import { SIZE, TEXT_HIGHLIGHT_THEME } from "common/components/text-highlight/types";
import { ButtonTheme } from "common/components/button/constants";
import { IconSizeType, PATH_SEPARATOR } from "common/constants";
import { SelectEl } from "common/components/select";
import { SelectTheme } from "common/components/select/constants";
import { UserIconEl } from "common/widgets/user-icon";
import { UserListEl } from "common/widgets/user-list";
import { getBrowseUrl } from "common/report/util/search";
import { formatDate, parseDate } from "common/util/date";
import { CONTEXT_PANEL_WIDTH } from "common/widgets/context-panel/constants";
import { BannerEl } from "common/components/banner";
import { BannerSize, BannerTheme, BannerType } from "common/components/banner/types";
import { DetailType } from "./types";
import { trackDiscoverBackLinkClicked } from "./util";
const MAX_DESCRIPTION_DISPLAY_LENGTH = 400;
var DEFAULT_ICON = /* @__PURE__ */ ((DEFAULT_ICON2) => {
  DEFAULT_ICON2["PERSON"] = `person`;
  DEFAULT_ICON2["FIRE"] = `fire`;
  DEFAULT_ICON2["PIE"] = `pie`;
  DEFAULT_ICON2["PULSE"] = `pulse`;
  return DEFAULT_ICON2;
})(DEFAULT_ICON || {});
const defaultMetrics = {
  person: {
    icon: "person" /* PERSON */,
    tooltip: ({
      t
    }) => t(`query-builder.context-menu.no-data`),
    value: `-`,
    rank: null,
    rankToolTip: ``
  },
  fire: {
    icon: "fire" /* FIRE */,
    tooltip: ({
      t
    }) => t(`query-builder.context-menu.no-data`),
    value: `-`,
    rank: null,
    rankToolTip: ``
  },
  pie: {
    icon: "pie" /* PIE */,
    tooltip: ({
      t
    }) => t(`query-builder.context-menu.no-data`),
    value: `-`,
    rank: null,
    rankToolTip: ``
  },
  pulse: {
    icon: "pulse" /* PULSE */,
    tooltip: ({
      t
    }) => t(`query-builder.context-menu.no-data`),
    value: `-`,
    rank: null,
    rankToolTip: ``
  }
};
export function template() {
  const {
    description
  } = this.state;
  const {
    backgroundColor,
    header,
    subHeader,
    path,
    entityIcon,
    showNewForFreeUsers,
    editItem,
    isUnsaved,
    hasUnsavedChanges,
    isRecentlyUsed,
    definitionDetails,
    definitionRenderer,
    details: rowDetails,
    metrics: measuredMetrics,
    excludedMetricIcons,
    extraInfo,
    hideMetricsSection,
    queriedByUsers,
    noteForFreeUsers,
    rowDetailsReplayPreviewRenderer,
    tags,
    imageInfo,
    verified,
    verifiedAt,
    verifiedBy
  } = this.params.data;
  const {
    t,
    print
  } = this.getContext(`i18n`);
  const projectIsFreePlan = this.attr(`project-is-free-plan`);
  const HeaderEl = () => {
    return /* @__PURE__ */ jsx("div", { sel: ".header", "data-sentry-component": "HeaderEl", "data-sentry-source-file": "template.tsx" }, entityIcon ? /* @__PURE__ */ jsx("div", { sel: ".header-icon" }, /* @__PURE__ */ jsx(SvgIconEl, { attrs: {
      icon: entityIcon
    } })) : null, /* @__PURE__ */ jsx("div", { sel: ".header-text" }, /* @__PURE__ */ jsx("div", { sel: ".heading", class: {
      "is-unsaved": isUnsaved
    } }, (path == null ? void 0 : path.length) > 0 && /* @__PURE__ */ jsx("span", { sel: ".path" }, [...path, ``].map((step) => print(step)).join(PATH_SEPARATOR)), /* @__PURE__ */ jsx("span", { sel: ".leaf" }, print(header), isUnsaved ? `*` : null)), subHeader ? /* @__PURE__ */ jsx("div", { sel: ".subheader" }, /* @__PURE__ */ jsx("div", { sel: ".text" }, print(subHeader))) : null), showNewForFreeUsers && projectIsFreePlan && /* @__PURE__ */ jsx("div", { sel: ".header-show-new-for-free-users" }, /* @__PURE__ */ jsx(TextHighlightEl, { text: t(`query-builder.context-menu.new`), theme: TEXT_HIGHLIGHT_THEME.INFORMATION })));
  };
  const DefinitionDetailSectionEl = ({
    details
  }) => {
    if (!details) {
      return null;
    }
    return /* @__PURE__ */ jsx("div", { sel: ".definition-detail-section", "data-sentry-component": "DefinitionDetailSectionEl", "data-sentry-source-file": "template.tsx" }, details.map((detail) => /* @__PURE__ */ jsx("div", { sel: ".detail" }, /* @__PURE__ */ jsx("div", { sel: ".detail-name" }, print(detail.name)), /* @__PURE__ */ jsx("div", { sel: ".detail-value" }, detail.value))));
  };
  const RowDetailSectionEl = ({
    details
  }) => {
    if (!(details == null ? void 0 : details.length)) {
      return null;
    }
    return /* @__PURE__ */ jsx("div", { sel: ".row-detail-section", "data-sentry-component": "RowDetailSectionEl", "data-sentry-source-file": "template.tsx" }, details.map((detail) => {
      var _a, _b, _c;
      return detail.type === DetailType.SessionReplayPreview && rowDetailsReplayPreviewRenderer ? /* @__PURE__ */ jsx("div", { sel: ".detail-preview" }, /* @__PURE__ */ jsx("div", { sel: ".detail-name" }, print(detail.name)), /* @__PURE__ */ jsx("div", { sel: ".detail-value" }, rowDetailsReplayPreviewRenderer(detail.value))) : /* @__PURE__ */ jsx("div", { sel: ".detail" }, /* @__PURE__ */ jsx("div", { sel: ".detail-name" }, print(detail.name)), /* @__PURE__ */ jsx("div", { sel: ".detail-value" }, detail.type === DetailType.User ? /* @__PURE__ */ jsx(UserIconEl, { sel: ".user-icon", attrs: {
        size: IconSizeType.XS,
        "include-label": true,
        ...detail.value
      } }) : detail.type === DetailType.UserList ? /* @__PURE__ */ jsx(UserListEl, { sel: ".user-list", attrs: {
        size: IconSizeType.XS,
        "max-icons-displayed": 4,
        users: detail.value
      } }) : detail.type === DetailType.TextHighlight ? detail.value.map((item) => /* @__PURE__ */ jsx(TextHighlightEl, { theme: TEXT_HIGHLIGHT_THEME.GENERIC, text: item, size: SIZE.SMALL })) : detail.type === DetailType.BrowseLink ? /* @__PURE__ */ jsx("a", { sel: ".backlink", className: {
        disabled: ((_a = detail.value) == null ? void 0 : _a.count) === 0
      }, attrs: {
        href: getBrowseUrl({
          queryParam: {
            "event-name-filter": (_b = detail.value) == null ? void 0 : _b.entityNameForBrowse
          },
          resetFilters: true
        })
      }, on: {
        click: () => {
          var _a2;
          trackDiscoverBackLinkClicked({
            eventName: header,
            reportsCount: (_a2 = detail.value) == null ? void 0 : _a2.count
          });
        }
      } }, t(`query-builder.context-menu.report-backlinks`, {
        count: +((_c = detail.value) == null ? void 0 : _c.count) || 0
      }), ` \u2197`) : detail.value));
    }));
  };
  const iconsToDisplay = ["pie" /* PIE */, "pulse" /* PULSE */];
  const metricDataByIcon = keyBy(measuredMetrics || [], `icon`);
  const metrics = iconsToDisplay.map((icon) => metricDataByIcon[icon] || defaultMetrics[icon]).filter((metric) => !(excludedMetricIcons == null ? void 0 : excludedMetricIcons.includes(metric.icon)));
  const FooterEl = () => {
    const verifiedSection = verified && verifiedBy ? /* @__PURE__ */ jsx("div", { sel: ".verify-icon-wrapper" }, /* @__PURE__ */ jsx(SvgIconEl, { sel: ".verify-icon", attrs: {
      icon: `verified-filled`
    } }), /* @__PURE__ */ jsx(TooltipEl, { attrs: {
      placement: Placement.Top
    } }, t(`query-builder.context-menu.verified-tooltip`, {
      date: formatDate(parseDate(verifiedAt, {
        iso: true
      })),
      name: verifiedBy
    }))) : void 0;
    const metricsSection = !hideMetricsSection && (metrics == null ? void 0 : metrics.length) ? /* @__PURE__ */ jsx("div", { sel: ".metrics-section" }, metrics.map((metric) => /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", { sel: ".metric", class: {
      "empty-metric": metric.value === `-`
    } }, /* @__PURE__ */ jsx("div", { sel: ".metric-icon", class: {
      "small-metric-icon": metric.icon === "pie" /* PIE */
    } }, /* @__PURE__ */ jsx(SvgIconEl, { attrs: {
      icon: metric.icon
    } })), /* @__PURE__ */ jsx("div", { sel: ".metric-value" }, metric.value || `-`), /* @__PURE__ */ jsx(TooltipEl, { attrs: {
      placement: Placement.Top
    } }, print(metric.tooltip)), metric.rank ? /* @__PURE__ */ jsx("div", { sel: ".metric-rank" }, /* @__PURE__ */ jsx(TooltipEl, { attrs: {
      placement: Placement.Top
    } }, print(metric.rankToolTip)), /* @__PURE__ */ jsx(TextHighlightEl, { theme: TEXT_HIGHLIGHT_THEME.INFORMATION, text: `# ${metric.rank}`, size: SIZE.SMALL })) : null)))) : void 0;
    const queriedBySection = (queriedByUsers == null ? void 0 : queriedByUsers.length) ? /* @__PURE__ */ jsx("div", { sel: ".recently-queried-users" }, /* @__PURE__ */ jsx(UserListEl, { attrs: {
      size: IconSizeType.Small,
      "max-icons-displayed": 2,
      users: queriedByUsers
    } })) : void 0;
    const entityActionsSection = this.helpers.getActionItems().length > 0 ? /* @__PURE__ */ jsx("div", { sel: ".entity-actions" }, /* @__PURE__ */ jsx(SelectEl, { attrs: {
      icon: {
        leftIcon: `ellipsis`
      },
      "icon-only": true,
      "menu-width": 200,
      theme: SelectTheme.Tertiary,
      "menu-alignment": Directions.Up,
      elref: `overflowMenu`
    }, class: {
      "row-actions": true
    }, props: {
      sections: [{
        items: this.helpers.getActionItems()
      }]
    }, on: {
      change: (ev) => this.helpers.handleActionClick(ev.detail.item)
    } })) : void 0;
    if (![verifiedSection, metricsSection, queriedBySection, entityActionsSection].some(Boolean)) {
      return void 0;
    }
    return /* @__PURE__ */ jsx("div", { sel: ".footer", "data-sentry-component": "FooterEl", "data-sentry-source-file": "template.tsx" }, verifiedSection, metricsSection, /* @__PURE__ */ jsx("div", { sel: ".footer-spacer" }), queriedBySection, entityActionsSection);
  };
  const footerText = projectIsFreePlan ? t(noteForFreeUsers) : ``;
  const descriptionAddPlaceholder = `+ ${t(`query-builder.context-menu.description-add-placeholder`)}`;
  return /* @__PURE__ */ jsx("div", { sel: ".root", style: {
    backgroundColor,
    "--context-panel-width": `${CONTEXT_PANEL_WIDTH}px`
  }, on: {
    mouseenter: this.helpers.handleMouseEnter,
    mouseleave: this.helpers.handleMouseLeave
  }, "data-sentry-component": "template", "data-sentry-source-file": "template.tsx" }, /* @__PURE__ */ jsx("div", { sel: ".body" }, hasUnsavedChanges && /* @__PURE__ */ jsx(BannerEl, { attrs: {
    theme: BannerTheme.Warn,
    type: BannerType.Embedded,
    size: BannerSize.Small,
    multiline: true,
    "no-wrap-content": false,
    "body-text": t(`query-builder.context-menu.unsaved-changes`)
  } }), /* @__PURE__ */ jsx("div", { sel: ".container" }, (() => {
    return /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx(HeaderEl, null), /* @__PURE__ */ jsx("div", { sel: ".content-body" }, isRecentlyUsed ? /* @__PURE__ */ jsx("div", { sel: ".recently-used" }, /* @__PURE__ */ jsx(SvgIconEl, { attrs: {
      icon: `time`
    } }), t(`query-builder.context-menu.recently-used`)) : null, (tags == null ? void 0 : tags.length) ? /* @__PURE__ */ jsx("div", { sel: ".tags" }, tags.map((tag) => /* @__PURE__ */ jsx(TextHighlightEl, { sel: ".tag", theme: TEXT_HIGHLIGHT_THEME.GENERIC, text: tag }))) : null, description || editItem ? /* @__PURE__ */ jsx("div", { sel: ".description" }, /* @__PURE__ */ jsx(RevealableTextEditEl, { attrs: {
      text: description,
      "hide-footer": true,
      placeholder: t(`query-builder.context-menu.description-placeholder`),
      "disable-edit": !editItem,
      size: InputSize.Medium
    }, on: {
      changedText: (ev) => this.helpers.handleTextEditorSave(ev, editItem)
    }, hook: {
      insert: this.helpers.editorInserted
    } }, (() => {
      return /* @__PURE__ */ jsx("div", { sel: ".description-content", attrs: {
        slot: `content`
      } }, editItem ? /* @__PURE__ */ jsx("div", { sel: ".description-text-wrapper" }, description ? this.helpers.formatText(description, {
        className: `description-text`,
        maxLength: MAX_DESCRIPTION_DISPLAY_LENGTH
      }) : descriptionAddPlaceholder, /* @__PURE__ */ jsx("div", { sel: ".editable-icon" }, /* @__PURE__ */ jsx(ButtonEl, { sel: ".edit-description-button", attrs: {
        icon: `edit`,
        theme: ButtonTheme.Tertiary
      } }))) : /* @__PURE__ */ jsx("div", { sel: ".description-text-wrapper" }, this.helpers.formatText(description, {
        className: `description-text`,
        maxLength: MAX_DESCRIPTION_DISPLAY_LENGTH
      })));
    })())) : null, imageInfo && imageInfo.length ? /* @__PURE__ */ jsx("div", { sel: ".entity-media-section" }, imageInfo.map((info) => /* @__PURE__ */ jsx("div", { sel: ".entity-media" }, info.imageUrl ? /* @__PURE__ */ jsx("img", { sel: ".entity-image", attrs: {
      src: info.imageUrl
    }, on: {
      click: () => this.helpers.showImageModalPromise(info.imageUrl)
    }, style: {
      cursor: `pointer`
    } }) : null))) : null, /* @__PURE__ */ jsx(DefinitionDetailSectionEl, { details: definitionDetails }), definitionRenderer && /* @__PURE__ */ jsx("div", { sel: ".definition-section" }, definitionRenderer()), /* @__PURE__ */ jsx(RowDetailSectionEl, { details: rowDetails }), extraInfo && extraInfo.length ? /* @__PURE__ */ jsx("div", { sel: ".extra-info" }, extraInfo.map((info) => /* @__PURE__ */ jsx("div", { sel: ".info" }, /* @__PURE__ */ jsx("div", { sel: ".icon" }, /* @__PURE__ */ jsx(SvgIconEl, { attrs: {
      icon: `info`
    } })), /* @__PURE__ */ jsx("div", { sel: ".value" }, print(info))))) : null));
  })())), footerText && /* @__PURE__ */ jsx("div", { sel: ".footer-text" }, footerText), /* @__PURE__ */ jsx(FooterEl, { "data-sentry-element": "FooterEl", "data-sentry-source-file": "template.tsx" }));
}
